import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  GraduationCap, 
  Users, 
  Heart, 
  BookOpen, 
  Sparkles, 
  Award,
  Phone,
  MapPin,
  Clock,
  Shield,
  Palette,
  Music,
  Utensils,
  CheckCircle2,
  ChevronRight,
  Instagram,
  Star,
  Target,
  Leaf,
  Sun,
  Smile,
  X,
  Facebook,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    program: '',
    message: ''
  });
  
  const [selectedProgram, setSelectedProgram] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Thank you! We will contact you soon.');
    setFormData({ name: '', email: '', phone: '', program: '', message: '' });
  };

  const programs = [
    {
      id: 'playgroup',
      title: 'Play Group',
      subtitle: '01',
      age: 'Ages 2-3',
      description: 'Designed for young learners to explore, play, and build foundational skills in a joyful environment',
      image: 'https://customer-assets.emergentagent.com/job_littlescholars-1/artifacts/vsvxg46b_Image_2.PNG',
      modalImage: 'https://customer-assets.emergentagent.com/job_scholars-playschool/artifacts/xle2ziki_Pre_school.jpg',
      color: 'from-orange-100 to-amber-100',
      borderColor: 'border-orange-400',
      icon: <Sparkles className="w-8 h-8 text-orange-600" />,
      imagePosition: 'right',
      details: [
        { title: 'Fine Motor & Practical Life Skills', desc: 'Gentle activities that strengthen hand muscles and develop independence' },
        { title: 'Vocabulary Development', desc: 'Fun games and storytelling to introduce new words and concepts' },
        { title: 'Basic Sounds & Phonics', desc: 'Early exposure to phonetic sounds to build a strong language foundation' }
      ]
    },
    {
      id: 'nursery',
      title: 'Nursery',
      subtitle: '02',
      age: 'Ages 3-4',
      description: 'Focuses on building foundational skills through hands-on learning and playful exploration',
      image: 'https://customer-assets.emergentagent.com/job_littlescholars-1/artifacts/b2wdjr0e_Image_3.PNG',
      modalImage: 'https://customer-assets.emergentagent.com/job_scholars-playschool/artifacts/kks1l2n8_Nursery.jpg',
      color: 'from-pink-100 to-rose-100',
      borderColor: 'border-pink-400',
      icon: <Heart className="w-8 h-8 text-pink-600" />,
      imagePosition: 'left',
      details: [
        { title: 'Fine Motor & Practical Life Skills', desc: 'Activities that strengthen coordination, independence, and everyday life skills' },
        { title: 'Language & Phonics', desc: 'Introduction to English through phonetics, storytelling, and vocabulary-building exercises' },
        { title: 'Mathematics & Cognitive Development', desc: 'Concept-based materials to make abstract ideas tangible, fostering logical thinking and problem-solving' }
      ]
    },
    {
      id: 'lkg',
      title: 'LKG',
      subtitle: '03',
      age: 'Ages 4-5',
      description: 'Children are introduced to more structured learning that builds on their curiosity and early skills',
      image: 'https://customer-assets.emergentagent.com/job_littlescholars-1/artifacts/3w5n001z_1000223240.jpg',
      modalImage: 'https://customer-assets.emergentagent.com/job_scholars-playschool/artifacts/tn2nzsta_Lkg.jpg',
      color: 'from-teal-100 to-cyan-100',
      borderColor: 'border-teal-500',
      icon: <BookOpen className="w-8 h-8 text-teal-600" />,
      imagePosition: 'right',
      details: [
        { title: 'Language Skills', desc: 'Expanding vocabulary, reading readiness, and early comprehension' },
        { title: 'Mathematics Concepts', desc: 'Using hands-on materials to understand numbers, patterns, and basic operations' },
        { title: 'Foundational Knowledge', desc: 'Introduction to simple concepts in science, geography, and history to spark curiosity' }
      ]
    },
    {
      id: 'ukg',
      title: 'UKG',
      subtitle: '04',
      age: 'Ages 5-6',
      description: 'Children explore slightly more complex ideas, preparing them for primary school while reinforcing critical thinking and independence',
      image: 'https://customer-assets.emergentagent.com/job_littlescholars-1/artifacts/hffyg0tf_Image_UKG.jpg',
      modalImage: 'https://customer-assets.emergentagent.com/job_scholars-playschool/artifacts/nl8y55p9_opy.jpg',
      color: 'from-purple-100 to-pink-100',
      borderColor: 'border-purple-500',
      icon: <GraduationCap className="w-8 h-8 text-purple-600" />,
      imagePosition: 'left',
      details: [
        { title: 'Advanced Language & Communication', desc: 'Building comprehension, sentence formation, and expressive skills' },
        { title: 'Mathematics & Logical Thinking', desc: 'Engaging with abstract concepts through practical, problem-solving activities' },
        { title: 'Integrated Learning', desc: 'Exploring basics of science, geography, and history in a hands-on, interactive way' }
      ]
    }
  ];

  const curriculumActivities = [
    { icon: <Target className="w-12 h-12" />, title: 'Exercises of Practical Life', description: 'Activities that help children develop independence, coordination, concentration, and everyday life skills through hands-on tasks.', color: 'from-red-200 to-rose-200', iconColor: 'text-red-600' },
    { icon: <Sparkles className="w-12 h-12" />, title: 'Sensorial Concepts', description: 'Materials and activities designed to refine the child\'s senses (sight, touch, sound, smell, and taste) and help them understand the world through observation and comparison.', color: 'from-purple-200 to-pink-200', iconColor: 'text-purple-600' },
    { icon: <BookOpen className="w-12 h-12" />, title: 'Language Enrichment', description: 'Activities that build vocabulary, listening, speaking, reading, and early writing skills through stories, conversations, and phonetic learning.', color: 'from-green-200 to-emerald-200', iconColor: 'text-green-600' },
    { icon: <Sparkles className="w-12 h-12" />, title: 'Arithmetic Concept', description: 'Introduction to numbers and basic mathematical ideas using concrete materials to help children understand counting, quantity, and simple operations.', color: 'from-orange-200 to-amber-200', iconColor: 'text-orange-600' },
    { icon: <Leaf className="w-12 h-12" />, title: 'Environmental Study', description: 'Learning about nature, community, culture, and surroundings to help children understand and appreciate the world around them.', color: 'from-teal-200 to-green-200', iconColor: 'text-teal-600' },
    { icon: <Palette className="w-12 h-12" />, title: 'Art & Craft', description: 'Creative activities that encourage imagination, self-expression, and fine motor development through drawing, painting, and craft work.', color: 'from-blue-200 to-cyan-200', iconColor: 'text-blue-600' }
  ];

  const features = [
    { icon: <Heart className="w-6 h-6" />, title: 'Small Class Sizes', description: 'Individual attention for every child', color: 'bg-rose-100 border-rose-400 text-rose-700' },
    { icon: <Shield className="w-6 h-6" />, title: 'Safe & Secure', description: 'CCTV monitored with child-safe campus', color: 'bg-blue-100 border-blue-400 text-blue-700' },
    { icon: <Users className="w-6 h-6" />, title: 'Qualified Teachers', description: 'Experienced Montessori educators', color: 'bg-purple-100 border-purple-400 text-purple-700' },
    { icon: <Sparkles className="w-6 h-6" />, title: 'Kreedo Lab', description: 'Interactive learning through innovative educational technology', color: 'bg-green-100 border-green-400 text-green-700' },
    { icon: <Sun className="w-6 h-6" />, title: 'Day Care Available', description: 'Full-day care with age-appropriate activities (2-6 years)', color: 'bg-amber-100 border-amber-400 text-amber-700' },
    { icon: <Award className="w-6 h-6" />, title: 'Cultural Events', description: 'Annual day and festival celebrations', color: 'bg-orange-100 border-orange-400 text-orange-700' }
  ];

  const googleReviews = [
    {
      name: 'Robin J',
      text: 'Little Scholars Pre School is a wonderful place for kids. My daughter, Shiloh, really enjoys going to school every day. The teachers Swetha Mam, Abi Mam, and Ananthi Mam are extremely caring and dedicated. They take very good care of the children and always give their best in teaching. The school conducts many fun and learning activities, which help children learn while enjoying themselves. There are many learning kits available, and kids actively use them to learn in a playful way. The environment is very friendly, joyful, and positive, making children feel comfortable and happy. The school is also clean and well-maintained. I am very happy with Little Scholars and highly recommend it to parents looking for a nurturing and engaging play school for their kids.',
      rating: 5,
      time: '2 months ago'
    },
    {
      name: 'Sri Mathi',
      text: 'I was looking for some big schools for my daughter Dhiya. Came to know about little scholars pre school. Initially with some hesitation I joined my daughter for class Ukg in this school. Now I was completely satisfied with her improvement. Her fluency in English, the way she pronounces, cursive writing, reading skill are upto the mark. Within 2 month i felt lot of changes in her reading style. She was trained well with phonics. Now she easily reads entire story without break. Abi mam and Swetha mam You are doing a great job with more dedication. Your way of response and respect you give to parents makes us comfortable. I dont know whether a big school could give this much individual care for my child. Thanks to teachers and care taker.',
      rating: 5,
      time: '2 years ago'
    },
    {
      name: 'Deepika P',
      text: 'I share my thoughts and heart touching experience here...im really lucky to put my son over here.... wonderful experience I ever had..they personally care for each children and staffs have very well teaching skills.i never leave him alone..the first time I allowed him to the school picnic.....but I\'m very blessed to have such a wonderful staff they care like parents...they took care of each and every single moment of kids. Once again thank every staffs and warm pleasure to giving wonderful feedback. Thanks one and all',
      rating: 5,
      time: '2 years ago'
    },
    {
      name: 'Raja S',
      text: 'My son name is hathvik. We know they are in good hands. They have a great curriculum and enjoy going there. The teachers are very nice and spend quality time with the kids. We get children\'s daily activity sheets and it\'s nice to know that they aren\'t just playing with toys all day long. They are actually doing constructive activities that are the building blocks as they move up through the program and move onto the next age level classroom. This preschool has everything a parent wants for their child. A Happy Parent!',
      rating: 5,
      time: '2 years ago'
    }
  ];

  const memories = [
    'https://customer-assets.emergentagent.com/job_scholars-playschool/artifacts/hdganawp_Our_home_3.PNG',
    'https://customer-assets.emergentagent.com/job_scholars-playschool/artifacts/xle2ziki_Pre_school.jpg',
    'https://customer-assets.emergentagent.com/job_scholars-playschool/artifacts/kks1l2n8_Nursery.jpg',
    'https://customer-assets.emergentagent.com/job_scholars-playschool/artifacts/tn2nzsta_Lkg.jpg',
    'https://customer-assets.emergentagent.com/job_scholars-playschool/artifacts/nl8y55p9_opy.jpg',
    'https://customer-assets.emergentagent.com/job_scholars-playschool/artifacts/nrltpwae_image.jpg'
  ];

  // Program Modal Component
  const ProgramModal = ({ program, onClose }) => {
    if (!program) return null;
    
    const isImageRight = program.imagePosition === 'right';
    
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="program-modal">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />
        
        {/* Modal Content */}
        <div className="relative bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 rounded-3xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
          {/* Close Button */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
            data-testid="modal-close-btn"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
          
          <div className={`grid md:grid-cols-2 gap-0 ${isImageRight ? '' : 'md:flex-row-reverse'}`}>
            {/* Content Side */}
            <div className={`p-8 md:p-12 ${isImageRight ? 'order-1' : 'order-1 md:order-2'}`}>
              <div className="text-6xl font-bold text-orange-200 mb-2">{program.subtitle}</div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{program.title}</h2>
              <p className="text-orange-600 font-semibold text-lg mb-6">{program.age}</p>
              
              <p className="text-gray-700 text-lg mb-8 leading-relaxed">
                {program.description}
              </p>
              
              <div className="space-y-6">
                {program.details.map((detail, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-bold text-gray-900">{detail.title}</p>
                      <p className="text-gray-600">{detail.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <a href="#contact" onClick={onClose}>
                <Button className="mt-8 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-lg rounded-full shadow-lg">
                  Enroll Your Kid
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              
              {/* Contact Numbers */}
              <div className="mt-6 flex flex-col space-y-2">
                <a href="tel:9345368029" className="flex items-center text-gray-700 hover:text-orange-600 transition-colors">
                  <Phone className="w-5 h-5 mr-2 text-orange-500" />
                  <span className="font-medium">+91 93453 68029</span>
                </a>
                <a href="tel:8667593874" className="flex items-center text-gray-700 hover:text-orange-600 transition-colors">
                  <Phone className="w-5 h-5 mr-2 text-orange-500" />
                  <span className="font-medium">+91 86675 93874</span>
                </a>
              </div>
            </div>
            
            {/* Image Side */}
            <div className={`relative h-64 md:h-auto min-h-[400px] ${isImageRight ? 'order-2' : 'order-2 md:order-1'}`}>
              <img 
                src={program.modalImage}
                alt={program.title}
                className="absolute inset-0 w-full h-full object-cover rounded-b-3xl md:rounded-b-none md:rounded-r-3xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-l from-black/30 to-transparent rounded-b-3xl md:rounded-b-none md:rounded-r-3xl" />
            </div>
          </div>
          
          {/* Footer */}
          <div className="bg-gray-900 text-center py-4 rounded-b-3xl">
            <p className="text-gray-400 text-sm">© 2024 Little Scholars PreSchool. All rights reserved.</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Program Modal */}
      {selectedProgram && (
        <ProgramModal 
          program={selectedProgram} 
          onClose={() => setSelectedProgram(null)} 
        />
      )}

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md shadow-sm z-50 border-b-4 border-orange-400">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_littlescholars-1/artifacts/i4qer4jx_1000223294.jpg"
                alt="Little Scholars Logo"
                className="h-16 w-16 rounded-full object-cover shadow-md"
              />
              <div>
                <h1 className="text-2xl font-bold text-purple-700">Little Scholars</h1>
                <p className="text-sm text-orange-600 font-semibold">PreSchool</p>
              </div>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#about" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">About</a>
              <a href="#programs" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">Programs</a>
              <a href="#curriculum" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">Curriculum</a>
              <a href="#contact" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">Contact</a>
            </nav>
            <a href="tel:9345368029">
              <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg">
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900">
        <div className="container mx-auto">
          <div className="text-center max-w-4xl mx-auto space-y-8">
            <div className="inline-block animate-bounce">
              <Smile className="w-16 h-16 text-amber-500" />
            </div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
              Welcome to
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500 mt-2">
                Little Scholars PreSchool
              </span>
            </h2>
            
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
              Our vision is to inspire children to become lifelong learners, global citizens and build self-confidence through a child-centred teaching and learning philosophy, prepared to lead meaningful lives in a global society
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 pt-4">
              <a href="#contact">
                <Button size="lg" className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-10 py-6 text-lg shadow-xl">
                  Book a School Visit
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <a href="https://api.whatsapp.com/send/?phone=919345368029&text&type=phone_number&app_absent=0" target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="px-10 py-6 text-lg border-2 border-green-500 text-green-400 hover:bg-green-500/10">
                  WhatsApp Us
                </Button>
              </a>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8 max-w-3xl mx-auto">
              <div className="bg-gradient-to-br from-gray-800 to-slate-800 rounded-2xl p-6 shadow-lg border border-orange-500/30">
                <p className="text-4xl font-bold text-orange-400">4+</p>
                <p className="text-gray-400 text-sm mt-1">Years</p>
              </div>
              <div className="bg-gradient-to-br from-gray-800 to-slate-800 rounded-2xl p-6 shadow-lg border border-purple-500/30">
                <p className="text-4xl font-bold text-purple-400">100%</p>
                <p className="text-gray-400 text-sm mt-1">Happy Parents</p>
              </div>
              <div className="bg-gradient-to-br from-gray-800 to-slate-800 rounded-2xl p-6 shadow-lg border border-pink-500/30">
                <p className="text-4xl font-bold text-pink-400">Safe</p>
                <p className="text-gray-400 text-sm mt-1">Environment</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section id="programs" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-900 to-gray-900">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-teal-500/20 text-teal-400 px-6 py-2 rounded-full text-sm font-bold mb-4 border border-teal-500/30">
              OUR EDUCATIONAL PROGRAMS
            </span>
            <h3 className="text-4xl lg:text-5xl font-bold text-white mb-4">Learning Programs</h3>
            <p className="text-lg text-gray-300">
              Our approach blends the best of Montessori philosophy with real-world learning — where children explore concepts, nature, and values that go far beyond the classroom
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {programs.map((program, index) => (
              <Card 
                key={index} 
                className={`border-2 ${program.borderColor} shadow-xl hover:shadow-2xl hover:shadow-amber-500/20 transition-all duration-500 hover:-translate-y-3 overflow-hidden group bg-gradient-to-br from-gray-800 to-slate-800 h-full cursor-pointer`}
                onClick={() => setSelectedProgram(program)}
                data-testid={`program-card-${program.id}`}
              >
                <CardContent className="p-0">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={program.image}
                      alt={program.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <h4 className="text-xl font-bold text-white">{program.title}</h4>
                      <p className="text-amber-400 font-semibold text-sm">{program.age}</p>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-4xl font-bold text-gray-600">{program.subtitle}</div>
                      <div className="bg-gray-700 rounded-full p-3 border-2 border-amber-500/30">
                        {program.icon}
                      </div>
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed line-clamp-3">{program.description}</p>
                    <div className="mt-4 flex items-center text-amber-400 font-semibold text-sm group-hover:text-amber-300">
                      <span>Learn More</span>
                      <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum Activities Section */}
      <section id="curriculum" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-purple-100 text-purple-700 px-6 py-2 rounded-full text-sm font-bold mb-4 border-2 border-purple-400">
              CURRICULUM HIGHLIGHTS
            </span>
            <h3 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">What do we teach?</h3>
            <p className="text-lg text-gray-600">
              Rooted in the heart and philosophy of the Montessori curriculum, our program focuses on four key areas of learning: Exercises of Practical Life, Sensorial Activities, Language, Arithmetic. In addition to these core areas, children participate in storytelling, songs, basic science explorations, and creative art and craft activities, all thoughtfully guided by our teachers.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {curriculumActivities.map((activity, index) => (
              <div key={index} className="group">
                <div className={`bg-gradient-to-br ${activity.color} rounded-3xl p-8 h-full hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border-4 border-white`}>
                  <div className={`w-20 h-20 bg-white rounded-2xl flex items-center justify-center mb-6 ${activity.iconColor} group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 shadow-lg`}>
                    {activity.icon}
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">{activity.title}</h4>
                  <p className="text-gray-700">{activity.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_littlescholars-1/artifacts/89pc2nfq_1000223249.jpg" 
                alt="Little Scholars students"
                className="rounded-3xl shadow-2xl w-full h-[500px] object-cover border-8 border-orange-200"
              />
            </div>
            
            <div className="space-y-6">
              <span className="inline-block bg-amber-100 text-amber-700 px-6 py-2 rounded-full text-sm font-bold border-2 border-amber-400">
                ABOUT US
              </span>
              <h3 className="text-4xl font-bold text-gray-900">Little Scholars PreSchool, Madurai</h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                At Little Scholars, we create a warm, nurturing environment where children learn through exploration, play, and discovery. Our <strong className="text-orange-600">Montessori approach</strong> ensures every child develops at their own pace.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Located in Koodal Nagar, Madurai, we provide quality early education with a focus on building confidence, creativity, and a love for learning.
              </p>
              
              <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl p-6 shadow-lg border-l-4 border-purple-500">
                <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <BookOpen className="w-6 h-6 text-purple-600 mr-2" />
                  Montessori Philosophy
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-1" />
                    <p className="text-gray-700">Child-centered learning at their own pace</p>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-1" />
                    <p className="text-gray-700">Hands-on materials for exploration</p>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-1" />
                    <p className="text-gray-700">Building independence and confidence</p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-orange-100 text-orange-700 px-6 py-2 rounded-full text-sm font-bold mb-4 border-2 border-orange-400">
              WHY CHOOSE US
            </span>
            <h3 className="text-4xl font-bold text-gray-900 mb-4">What Makes Us Special</h3>
            <p className="text-lg text-gray-600">
              Quality education with care, safety, and personal attention
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <div key={index} className="group">
                <div className={`${feature.color} rounded-2xl p-8 h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-4`}>
                  <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-md">
                    {feature.icon}
                  </div>
                  <h4 className="text-xl font-bold mb-3">{feature.title}</h4>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-blue-100 text-blue-700 px-6 py-2 rounded-full text-sm font-bold mb-4 border-2 border-blue-400">
              PARENT REVIEWS
            </span>
            <h3 className="text-4xl font-bold text-gray-900 mb-4">What Parents Say</h3>
            <p className="text-lg text-gray-600">
              Trusted by families across Madurai
            </p>
            <a 
              href="https://www.google.com/search?q=Little+scholars+preschool+madurai"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center mt-4 text-blue-600 hover:text-blue-700 font-medium"
            >
              <span>View all Google Reviews</span>
              <ExternalLink className="w-4 h-4 ml-2" />
            </a>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {googleReviews.map((review, index) => (
              <Card key={index} className="border-2 border-gray-200 shadow-lg hover:shadow-xl transition-shadow duration-300 bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {review.name.charAt(0)}
                    </div>
                    <div className="ml-3">
                      <p className="font-semibold text-gray-900 text-sm">{review.name}</p>
                      <p className="text-xs text-gray-500">{review.time}</p>
                    </div>
                  </div>
                  <div className="flex mb-3">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-gray-600 text-sm line-clamp-4">"{review.text}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Home Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-blue-100 text-blue-700 px-6 py-2 rounded-full text-sm font-bold mb-4 border-2 border-blue-400">
              GALLERY
            </span>
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Our Home</h3>
            <p className="text-lg text-gray-600">
              Where learning begins with love
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              {/* School Building Image */}
              <div className="group relative overflow-hidden rounded-2xl shadow-lg aspect-square">
                <img 
                  src="https://customer-assets.emergentagent.com/job_scholars-playschool/artifacts/mbpo8wmw_Our%20home.jpg"
                  alt="Our Home - School Building"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              {/* Video with highlight */}
              <div className="relative rounded-2xl shadow-2xl overflow-hidden border-4 border-purple-400 bg-gray-900">
                <div className="absolute top-4 left-4 z-10 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-2">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  VIDEO
                </div>
                <video 
                  src="https://customer-assets.emergentagent.com/job_scholars-playschool/artifacts/7ef534b8_WhatsApp%20Video%202026-03-15%20at%2012.48.34%20PM.mp4"
                  className="w-full aspect-video object-cover"
                  controls
                  muted
                  playsInline
                  poster="https://customer-assets.emergentagent.com/job_scholars-playschool/artifacts/mbpo8wmw_Our%20home.jpg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Kreedo Lab Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-50 to-teal-50">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-green-100 text-green-700 px-6 py-2 rounded-full text-sm font-bold mb-4 border-2 border-green-400">
              GALLERY
            </span>
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Our Kreedo Lab</h3>
            <p className="text-lg text-gray-600">
              Interactive learning through innovative educational technology
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            {/* First Row - 3 images */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="group relative overflow-hidden rounded-2xl shadow-lg aspect-square">
                <img 
                  src="https://customer-assets.emergentagent.com/job_scholars-playschool/artifacts/nv38qb4n_Lab1.jpg"
                  alt="Kreedo Lab 1"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              <div className="group relative overflow-hidden rounded-2xl shadow-lg aspect-square">
                <img 
                  src="https://customer-assets.emergentagent.com/job_scholars-playschool/artifacts/nk1mk8sx_Lab2.jpg"
                  alt="Kreedo Lab 2"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              <div className="group relative overflow-hidden rounded-2xl shadow-lg aspect-square">
                <img 
                  src="https://customer-assets.emergentagent.com/job_scholars-playschool/artifacts/ndgdpkne_Lab3.jpg"
                  alt="Kreedo Lab 3"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </div>
            {/* Second Row - 2 images centered */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
              <div className="group relative overflow-hidden rounded-2xl shadow-lg aspect-square">
                <img 
                  src="https://customer-assets.emergentagent.com/job_scholars-playschool/artifacts/yimj7gi1_Lab%205.jpg"
                  alt="Kreedo Lab 4"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              <div className="group relative overflow-hidden rounded-2xl shadow-lg aspect-square">
                <img 
                  src="https://customer-assets.emergentagent.com/job_scholars-playschool/artifacts/fkopbsr1_Lab%206.jpg"
                  alt="Kreedo Lab 5"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Memories Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-pink-50 to-purple-50">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-pink-100 text-pink-700 px-6 py-2 rounded-full text-sm font-bold mb-4 border-2 border-pink-400">
              GALLERY
            </span>
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Our Memories</h3>
            <p className="text-lg text-gray-600">
              Precious moments from our school activities
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            {/* First Row - 3 images */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {memories.slice(0, 3).map((img, index) => (
                <div key={index} className="group relative overflow-hidden rounded-2xl shadow-lg aspect-square">
                  <img 
                    src={img}
                    alt={`Memory ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              ))}
            </div>
            {/* Second Row - 3 images */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {memories.slice(3, 6).map((img, index) => (
                <div key={index + 3} className="group relative overflow-hidden rounded-2xl shadow-lg aspect-square">
                  <img 
                    src={img}
                    alt={`Memory ${index + 4}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-100 via-amber-100 to-yellow-100">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div className="space-y-8">
              <div>
                <span className="inline-block bg-green-100 text-green-700 px-6 py-2 rounded-full text-sm font-bold mb-4 border-2 border-green-400">
                  GET IN TOUCH
                </span>
                <h3 className="text-4xl font-bold text-gray-900 mb-4">Contact Us</h3>
                <p className="text-lg text-gray-700">
                  Visit us or call to schedule a school tour!
                </p>
              </div>
              
              <div className="space-y-6">
                <a 
                  href="https://www.google.com/maps/dir//Little+Scholars+Pre+School,+21,+4th+St,+Appathurai+Nagar,+Koodal+Nagar,+Madurai,+Tamil+Nadu+625018/@12.9526616,77.5275458,15z/data=!4m8!4m7!1m0!1m5!1m1!1s0x3b00c973b13f2f05:0xb6b4330cafb24a3b!2m2!1d78.098833!2d9.957815?entry=ttu&g_ep=EgoyMDI2MDMxMS4wIKXMDSoASAFQAw%3D%3D"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <div className="flex items-start space-x-4 bg-white rounded-2xl p-6 shadow-md border-l-4 border-orange-500 hover:shadow-lg transition-shadow cursor-pointer">
                    <MapPin className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-bold text-gray-900 text-lg flex items-center">
                        Visit Us
                        <ExternalLink className="w-4 h-4 ml-2 text-orange-500" />
                      </p>
                      <p className="text-gray-700">No 21, Appadurai 4th Street<br/>Koodal Nagar, Madurai</p>
                    </div>
                  </div>
                </a>
                
                <div className="flex items-start space-x-4 bg-white rounded-2xl p-6 shadow-md border-l-4 border-purple-500">
                  <Phone className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-bold text-gray-900 text-lg">Call Us</p>
                    <a href="tel:9345368029" className="text-gray-700 hover:text-purple-600 block">
                      +91 93453 68029 (Abirami)
                    </a>
                    <a href="tel:8667593874" className="text-gray-700 hover:text-purple-600 block">
                      +91 86675 93874 (Swetha)
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 bg-white rounded-2xl p-6 shadow-md border-l-4 border-pink-500">
                  <Clock className="w-6 h-6 text-pink-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-bold text-gray-900 text-lg">Timings</p>
                    <p className="text-gray-700">Monday - Saturday<br/>9:00 AM - 6:00 PM</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 bg-white rounded-2xl p-6 shadow-md border-l-4 border-gradient-to-r from-pink-500 to-purple-500">
                  <Instagram className="w-6 h-6 text-pink-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-bold text-gray-900 text-lg">Instagram</p>
                    <a 
                      href="https://www.instagram.com/littlescholarsmadurai" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-700 hover:text-pink-600"
                    >
                      @littlescholarsmadurai
                    </a>
                  </div>
                </div>

                <a 
                  href="https://www.facebook.com/littlescholarsmadurai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <div className="flex items-start space-x-4 bg-white rounded-2xl p-6 shadow-md border-l-4 border-blue-600 hover:shadow-lg transition-shadow cursor-pointer">
                    <Facebook className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-bold text-gray-900 text-lg flex items-center">
                        Facebook
                        <ExternalLink className="w-4 h-4 ml-2 text-blue-500" />
                      </p>
                      <p className="text-gray-700 hover:text-blue-600">Little Scholars Madurai</p>
                    </div>
                  </div>
                </a>
              </div>
            </div>

            <Card className="border-4 border-orange-400 shadow-2xl bg-white">
              <CardContent className="p-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-6">Admission Inquiry</h4>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <Input 
                    placeholder="Parent's Name *"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className="h-12 border-2 border-gray-300 focus:border-orange-500"
                    data-testid="inquiry-name"
                  />
                  <Input 
                    type="tel"
                    placeholder="Phone Number *"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                    className="h-12 border-2 border-gray-300 focus:border-orange-500"
                    data-testid="inquiry-phone"
                  />
                  <Input 
                    type="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="h-12 border-2 border-gray-300 focus:border-orange-500"
                    data-testid="inquiry-email"
                  />
                  <select 
                    value={formData.program}
                    onChange={(e) => setFormData({...formData, program: e.target.value})}
                    className="w-full h-12 px-3 rounded-md border-2 border-gray-300 focus:border-orange-500"
                    required
                    data-testid="inquiry-program"
                  >
                    <option value="">Select Program *</option>
                    <option value="Play Group">Play Group (2-3 years)</option>
                    <option value="Nursery">Nursery (3-4 years)</option>
                    <option value="LKG">LKG (4-5 years)</option>
                    <option value="UKG">UKG (5-6 years)</option>
                    <option value="Day Care">Day Care (2-6 years)</option>
                  </select>
                  <Textarea 
                    placeholder="Your Message"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    rows={4}
                    className="border-2 border-gray-300 focus:border-orange-500"
                    data-testid="inquiry-message"
                  />
                  <Button type="submit" className="w-full h-12 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white text-lg font-semibold" data-testid="inquiry-submit">
                    Submit Inquiry
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-purple-600 to-pink-600 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_littlescholars-1/artifacts/i4qer4jx_1000223294.jpg"
                  alt="Little Scholars Logo"
                  className="h-12 w-12 rounded-full object-cover"
                />
                <div>
                  <h5 className="font-bold text-lg">Little Scholars</h5>
                  <p className="text-sm text-purple-100">PreSchool</p>
                </div>
              </div>
              <p className="text-purple-100">
                Nurturing young minds for a brighter tomorrow.
              </p>
            </div>
            
            <div>
              <h5 className="font-bold text-lg mb-4">Quick Links</h5>
              <ul className="space-y-2">
                <li><a href="#about" className="text-purple-100 hover:text-white transition-colors">About Us</a></li>
                <li><a href="#programs" className="text-purple-100 hover:text-white transition-colors">Our Programs</a></li>
                <li><a href="#curriculum" className="text-purple-100 hover:text-white transition-colors">Curriculum</a></li>
                <li><a href="#contact" className="text-purple-100 hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-bold text-lg mb-4">Follow Us</h5>
              <div className="flex space-x-4 mb-4">
                <a 
                  href="https://www.instagram.com/littlescholarsmadurai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a 
                  href="https://www.facebook.com/littlescholarsmadurai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              </div>
              <ul className="space-y-3 text-purple-100">
                <li>No 21, Appadurai 4th Street, Koodal Nagar, Madurai</li>
                <li><a href="tel:9345368029" className="hover:text-white transition-colors">+91 93453 68029</a></li>
                <li><a href="tel:8667593874" className="hover:text-white transition-colors">+91 86675 93874</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-purple-400 pt-8 text-center text-purple-100">
            <p>© 2024 Little Scholars PreSchool. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a 
        href="https://api.whatsapp.com/send/?phone=919345368029&text&type=phone_number&app_absent=0" 
        target="_blank" 
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-16 h-16 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-all duration-300 z-50"
        data-testid="whatsapp-btn"
      >
        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>
    </div>
  );
};

export default Home;
